// uses FaxesViewContext to prompt the user to select a

import { ArrowBackIcon } from '@chakra-ui/icons'
import {
  Badge,
  Box,
  CircularProgress,
  Collapse,
  HStack,
  IconButton,
  Text,
  VStack,
} from '@chakra-ui/react'
import { AppContext, PopUpMessageContext, useCachedUser, usePracticeAccess } from '@hb/react'
import {
  Assessment,
  colors,
  DropdownField,
  FieldTypes,
  getAssessmentName,
  getDateString,
  getPracticeAssessmentCollectionPath,
} from '@hb/shared'
import { collection, Query, query, where } from 'firebase/firestore'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { ASSESSMENTS_REF } from '../../backend/db/collections'
import { db } from '../../backend/db/db'
import { useQuery } from '../../hooks/backend/useQuery'
import { ActionLog } from '../ActionLog'
import { StandaloneInput } from '../forms/Input'
import { CollapseHorizontal } from '../shared/CollapseHorizontal'
import { SearchedPatientPreview, SearchUsers } from '../Users/SearchUsers'

const SelectPatientAssessment = ({
  patientId,
  setPatientId,
  assessmentId,
  onSubmit,
  onBack,
  submitting,
  noneOptionText,
  noPatientEdit,
}: {
  patientId: string | null
  setPatientId: (pId: string | null) => void
  assessmentId?: string | null
  onBack?: () => void
  onSubmit: (submitted: string) => void
  submitting: boolean
  noneOptionText?: string
  noPatientEdit?: boolean
}) => {
  // const {
  //   faxAssignment: {
  //     onSubmit,
  //     submitting,
  //     assigningToPatientId,
  //     assignToPatient,
  //   },
  // } = useContext(FaxesViewContext)

  const { appName } = useContext(AppContext)
  const { selectedPracticeId } = usePracticeAccess()
  const queryRef = useMemo(() => {
    if (!patientId) return null
    if (appName === 'providers-app' && !selectedPracticeId) return null
    if (appName === 'app') {
      return query(ASSESSMENTS_REF, where('patientId', '==', patientId))
    }
    return selectedPracticeId
      ? (query(
          collection(db, getPracticeAssessmentCollectionPath(selectedPracticeId)),
          where('patientId', '==', patientId),
        ) as Query<Assessment>)
      : null
  }, [patientId, appName, selectedPracticeId])
  const { data: assessments, loading: assessmentsLoading } = useQuery<Assessment>(queryRef)

  const field = useMemo<DropdownField>(
    () => ({
      type: FieldTypes.DROPDOWN,
      placeholder: 'Select an assessment',
      options: [
        ...Object.entries(assessments ?? {}).map(([id, assessment]) => ({
          text: getAssessmentName(assessment),
          id,
        })),
        ...(noneOptionText ? [{ id: 'NONE', text: noneOptionText }] : []),
      ],
      renderOption: ({ id }) => {
        if (id === 'NONE') {
          return (
            <Text p={2} fontFamily="Open Sans">
              {noneOptionText}
            </Text>
          )
        }
        const assessment = assessments?.[id]
        return (
          <VStack align="flex-start" spacing={1} py={2} px={3}>
            {assessment ? (
              <>
                <HStack>
                  <ActionLog
                    on={assessment.createdOn}
                    by={assessment.createdBy}
                    action="Created"
                    group={assessment.createdByGroup}
                  />
                </HStack>
                <HStack>
                  <Text fontFamily="Open Sans">{getAssessmentName(assessment)}</Text>
                  {assessment.archivedOn ? (
                    <Badge colorScheme="red">
                      Archived on {getDateString(assessment.archivedOn, 'short')}
                    </Badge>
                  ) : null}
                </HStack>
              </>
            ) : (
              <Text> No assessment with id {id}</Text>
            )}
          </VStack>
        )
      },
    }),
    [assessments, noneOptionText],
  )
  let body = null
  if (submitting) {
    body = (
      <HStack p={2} w="100%">
        <CircularProgress size={5} isIndeterminate color={colors.green.hex} />
        <Text>Submitting changes...</Text>
      </HStack>
    )
  } else if (assessmentsLoading) {
    body = (
      <HStack p={2} w="100%">
        <CircularProgress size={5} isIndeterminate color={colors.green.hex} />
        <Text>Getting assessments...</Text>
      </HStack>
    )
  } else {
    body = (
      <StandaloneInput
        field={field}
        theme="detailed"
        onChange={id => onSubmit(id)}
        value={assessmentId}
      />
    )
  }
  return (
    <Box bg="gray.50" borderRadius={4} boxShadow="md">
      <HStack w="100%">
        <Box flex={1} minW="0">
          {body}
        </Box>
        {noPatientEdit && !onBack ? null : (
          <CollapseHorizontal width={36} in={!submitting}>
            <IconButton
              size="sm"
              ml="auto"
              aria-label="Back"
              variant="ghost"
              icon={<ArrowBackIcon />}
              onClick={() => {
                if (!noPatientEdit) setPatientId(null)
                if (onBack) {
                  onBack()
                }
              }}
            />
          </CollapseHorizontal>
        )}
      </HStack>
    </Box>
  )
}

// patient and then an assessment belonging to that patient
export const SearchAssessments = ({
  onSubmit,
  onBack,
  assessmentId,
  patientId: initPatientId,
  noneOptionText,
  noPatientPreview,
}: {
  onSubmit: (assessmentId: string | null, patientId: string | null) => Promise<void>
  onBack?: () => void
  assessmentId?: string | null
  patientId?: string | null
  noPatientPreview?: boolean
  noneOptionText?: string
}) => {
  // const {
  //   faxAssignment: {
  //     assigningToFaxId,
  //     assigningToPatientId,
  //     assignToFax,
  //     assignToPatient,
  //   },
  // } = useContext(FaxesViewContext)

  const [patientId, setPatientId] = useState<string | null>(initPatientId ?? null)
  useEffect(() => {
    setPatientId(initPatientId ?? null)
  }, [initPatientId])
  const [submitting, setSubmitting] = useState(false)
  const { data: patient, loading: patientLoading } = useCachedUser(patientId)
  const { processResponse } = useContext(PopUpMessageContext)
  const handleSubmit = useCallback(
    async (id: string) => {
      setSubmitting(true)
      try {
        await onSubmit(id, patientId)
      } catch (err: any) {
        processResponse({ error: err?.message || 'An error occurred' })
      }
      setSubmitting(false)
    },
    [onSubmit, processResponse, patientId],
  )

  return (
    <Box w="100%">
      <Collapse style={{ width: '100%' }} in={!!patientId}>
        <Box p={1} w="100%">
          {patientId && !noPatientPreview ? (
            <SearchedPatientPreview patient={patient} loading={patientLoading} />
          ) : null}
          <SelectPatientAssessment
            onSubmit={handleSubmit}
            submitting={submitting}
            onBack={onBack}
            patientId={patientId}
            noPatientEdit={!!initPatientId}
            assessmentId={assessmentId}
            setPatientId={setPatientId}
            noneOptionText={noneOptionText}
          />
        </Box>
      </Collapse>
      <Collapse in={!patientId}>
        <Box w="100%" p={1}>
          <SearchUsers userTypeName="patient" onSelect={id => setPatientId(id)} />
        </Box>
      </Collapse>
    </Box>
  )
}
